import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { toast } from 'sonner';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  CreateEventDokumentTypMappingRequest,
  GetEventDokumentTypMappingProtocolByExternalId200ResponseInner,
} from './__generated';
import type { GetEventDokumentTypMappings200ResponseDataInner } from './__generated';
import { queryKeys } from './queryKeys';
import { useApiClient } from './useApiClient';

export const useEventDokumentTypMappings = () => {
  const { eventDokumentTypMappingsApi } = useApiClient();

  return useQuery<GetEventDokumentTypMappings200ResponseDataInner[]>({
    queryKey: queryKeys.konfiguration.eventDokumentTypMappings(),
    queryFn: async () => {
      const response =
        await eventDokumentTypMappingsApi.getEventDokumentTypMappings();
      return response.data.data;
    },
  });
};

export const useEventDokumentTypMappingByExternalId = (externalId: string) => {
  const { eventDokumentTypMappingsApi } = useApiClient();

  return useQuery<GetEventDokumentTypMappings200ResponseDataInner>({
    queryKey:
      queryKeys.konfiguration.eventDokumentTypMappingByExternalId(externalId),
    queryFn: async () => {
      const response =
        await eventDokumentTypMappingsApi.getEventDokumentTypMappingByExternalId(
          {
            externalId: externalId,
          },
        );
      return response.data;
    },
  });
};

export const useAktivierePausiereDokumenttyp = () => {
  const { eventDokumentTypMappingsApi } = useApiClient();
  const queryClient = useQueryClient();

  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      externalId: string;
      dokumentTyp: string;
      aktiviert: boolean;
    }) => {
      const response =
        await eventDokumentTypMappingsApi.activateOrDisableEventDokumentTypMappingByExternalId(
          {
            externalId: params.externalId,
            aktiviert: params.aktiviert,
            user: user?.email || '',
          },
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.eventDokumentTypMappings(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.eventDokumentTypMappingByExternalId(
          variables.externalId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey:
          queryKeys.konfiguration.eventDokumentTypMappingProtocolByExternalId(
            variables.externalId,
          ),
      });
      toast.success(
        `Die Verarbeitung für den Dokumententyp ${variables.dokumentTyp} wurde ${variables.aktiviert ? 'aktiviert' : 'pausiert'}.`,
      );
    },
    onError: (error) => {
      toast.error(
        `Fehler beim Aktivieren oder Deaktivieren eines Dokumententyps.`,
      );

      // eslint-disable-next-line no-console
      console.error('Failed to toggle documentType:', error);
    },
  });
};

export const useCreateDokumenttyp = () => {
  const { eventDokumentTypMappingsApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: CreateEventDokumentTypMappingRequest) => {
      const response =
        await eventDokumentTypMappingsApi.createEventDokumentTypMapping({
          createEventDokumentTypMappingRequest: params,
          user: user?.email || '',
        });
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success(`Dokumenttyp ${variables.dokumentTyp} wurde erstellt.`);
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.eventDokumentTypMappings(),
      });
    },
    onError: (error) => {
      toast.error(`Fehler beim Erstellen des Dokumenttyps.`);
      // eslint-disable-next-line no-console
      console.error('Failed to create documentType:', error);
    },
  });
};

export const useDeleteDokumenttyp = () => {
  const { eventDokumentTypMappingsApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (externalId: string) => {
      const response =
        await eventDokumentTypMappingsApi.deleteEventDokumentTypMapping({
          externalId: externalId,
          user: user?.email || '',
        });
      return response.data;
    },
    onSuccess: (_, externalId) => {
      toast.success(
        `Der Dokumenttyp mit der externen Id: ${externalId} wurde gelöscht.`,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.eventDokumentTypMappings(),
      });
      queryClient.invalidateQueries({
        queryKey:
          queryKeys.konfiguration.eventDokumentTypMappingByExternalId(
            externalId,
          ),
      });
    },
    onError: (error) => {
      toast.error(`Fehler beim Löschen des Dokumenttyps.`);
      // eslint-disable-next-line no-console
      console.error('Failed to delete documentType:', error);
    },
  });
};

export const useUpdateDokumenttyp = () => {
  const { eventDokumentTypMappingsApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (
      params: CreateEventDokumentTypMappingRequest & { externalId: string },
    ) => {
      const { externalId, ...rest } = params;
      const response =
        await eventDokumentTypMappingsApi.updateEventDokumentTypMappingByExternalId(
          {
            externalId,
            createEventDokumentTypMappingRequest: rest,
            user: user?.email || '',
          },
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success(`Dokumenttyp ${variables.dokumentTyp} wurde aktualisiert.`);
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.eventDokumentTypMappings(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.eventDokumentTypMappingByExternalId(
          variables.externalId,
        ),
      });
    },
    onError: (error) => {
      toast.error(`Fehler beim Aktualisieren des Dokumenttyps.`);
      // eslint-disable-next-line no-console
      console.error('Failed to update dokumenttyp:', error);
    },
  });
};

export const useEventDokumentTypProtocolByExternalId = (externalId: string) => {
  const { eventDokumentTypMappingsApi } = useApiClient();

  return useQuery<
    GetEventDokumentTypMappingProtocolByExternalId200ResponseInner[]
  >({
    queryKey:
      queryKeys.konfiguration.eventDokumentTypMappingProtocolByExternalId(
        externalId,
      ),
    queryFn: async () => {
      const response =
        await eventDokumentTypMappingsApi.getEventDokumentTypMappingProtocolByExternalId(
          {
            externalId: externalId,
          },
        );
      return response.data;
    },
  });
};

export const useRestoreEventDokumentTypByExternalId = () => {
  const { eventDokumentTypMappingsApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (externalId: string) => {
      const response =
        await eventDokumentTypMappingsApi.restoreEventDokumentTypMappingByExternalId(
          {
            externalId: externalId,
            user: user?.email || '',
          },
        );
      return response.data;
    },
    onSuccess: (_, externalId) => {
      toast.success(
        `Der Dokumenttyp mit der externen Id: ${externalId} wurde wiederhergestellt.`,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.konfiguration.eventDokumentTypMappings(),
      });
      queryClient.invalidateQueries({
        queryKey:
          queryKeys.konfiguration.eventDokumentTypMappingByExternalId(
            externalId,
          ),
      });
    },
    onError: (error) => {
      toast.error(`Fehler beim Wiederherstellen des Dokumenttyps.`);
      // eslint-disable-next-line no-console
      console.error('Failed to restore dokumenttyp:', error);
    },
  });
};
